import React, {
  DetailedHTMLProps,
  AnchorHTMLAttributes,
  forwardRef,
} from 'react';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from '@react-aria/utils';
import classnames from 'classnames';
import { ExternalLinkIcon as ExternalLinkSvg } from '@stpkg/stand-react';

export type RSAnchorProps = DetailedHTMLProps<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>;

export const RSAnchor = forwardRef<HTMLAnchorElement, RSAnchorProps>(
  ({ children, ...props }, ref) => {
    const { isFocusVisible, focusProps } = useFocusRing();

    const isExternal =
      props.href?.startsWith('https:') ||
      props.href?.startsWith('http:') ||
      props.href?.startsWith('mailto:');

    const anchorOpt = {
      target: isExternal ? '_blank' : undefined,
      rel: isExternal ? 'noopener noreferrer' : undefined,
    };

    const color = '#0066FF';

    return (
      <>
        <span
          className={classnames(
            'wrapper',
            isFocusVisible && 'focus-visible',
            'inline-block',
            'relative',
            'before:transition',
          )}
        >
          <a
            target={anchorOpt.target}
            rel={anchorOpt.rel}
            {...mergeProps(props, focusProps)}
            ref={ref}
            className={classnames(
              'link',
              'relative',
              'inline-flex',
              'underline',
              'underline-offset-4',
              'decoration-transparent',
              'transition-all',
              'outline-none',
              'focus:outline-none',
            )}
          >
            {children}
            {isExternal && (
              <ExternalLinkSvg
                className="fill-blue-700"
                viewBox="0 0 48 48"
                width={20}
                height={20}
              />
            )}
          </a>
        </span>
        <style jsx>{`
          .wrapper::before {
            content: '';
            box-sizing: border-box;
            display: block;
            position: absolute;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
            border-style: solid;
            border-width: 2px;
            border-color: transparent;
            border-radius: 5px;
          }

          .wrapper.focus-visible::before {
            border-color: ${color};
          }

          .link {
            color: ${color};
          }
          .link:hover {
            text-decoration-color: ${color};
          }
        `}</style>
      </>
    );
  },
);

RSAnchor.displayName = 'RSAnchor';
